import React, { useEffect, useMemo, useState } from 'react';
import AdminSidebar from '../components/AdminSidebar';
import hygeaLogo from '../../assets/hygea.png';

import { API_BASE } from '../../config/runtime';

const TicketManagement = () => {
  const [tickets, setTickets] = useState([]);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [solvingId, setSolvingId] = useState(null);
  const [modalError, setModalError] = useState('');

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    if (selectedTicket) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = originalOverflow || '';
    }
    return () => {
      document.body.style.overflow = originalOverflow || '';
    };
  }, [selectedTicket]);

  useEffect(() => {
    const fetchTickets = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(`${API_BASE}/get_tickets.php`);
        const data = await response.json();
        if (!data.success) {
          setError(data.message || 'Failed to load tickets');
          return;
        }
        setTickets(data.tickets || []);
      } catch (err) {
        setError(`Failed to load tickets: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchTickets();
  }, []);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Urgent': return 'bg-rose-100 text-rose-700 border border-rose-200';
      case 'High':   return 'bg-red-100 text-red-700 border border-red-200';
      case 'Medium': return 'bg-amber-100 text-amber-700 border border-amber-200';
      case 'Low':    return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
      default:       return 'bg-gray-100 text-gray-700 border border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending':  return 'bg-amber-100 text-amber-700 border border-amber-200';
      case 'Resolved': return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
      default:         return 'bg-gray-100 text-gray-700 border border-gray-200';
    }
  };

  const normalizedTickets = useMemo(() =>
    tickets.map((t) => ({
      id: t.ticket_id,
      user: t.sender_name || 'Unknown',
      branch: t.branch_name || 'Unassigned Branch',
      subject: t.subject || '',
      message: t.message || '',
      type: t.ticket_type || 'Ticket',
      priority: t.priority || 'Medium',
      status: t.status || 'Pending',
      date: t.created_at ? String(t.created_at).slice(0, 10) : '',
      hasAttachment: Boolean(Number(t.has_attachment || 0)),
    })),
  [tickets]);

  const filteredTickets = normalizedTickets.filter((ticket) => {
    const matchesStatus = statusFilter === 'All' || ticket.status === statusFilter;
    const q = query.trim().toLowerCase();
    if (!q) return matchesStatus;
    return (
      matchesStatus &&
      (`${ticket.id}`.includes(q) ||
        ticket.user.toLowerCase().includes(q) ||
        ticket.branch.toLowerCase().includes(q) ||
        ticket.subject.toLowerCase().includes(q) ||
        ticket.message.toLowerCase().includes(q) ||
        ticket.priority.toLowerCase().includes(q) ||
        ticket.status.toLowerCase().includes(q))
    );
  });

  const ticketRows  = filteredTickets.filter((t) => t.type !== 'Request');
  const requestRows = filteredTickets.filter((t) => t.type === 'Request');

  const pendingCount  = normalizedTickets.filter((t) => t.status === 'Pending').length;
  const resolvedCount = normalizedTickets.filter((t) => t.status === 'Resolved').length;
  const requestCount  = normalizedTickets.filter((t) => t.type === 'Request').length;
  const ticketCount   = normalizedTickets.filter((t) => t.type !== 'Request').length;

  const openTicketModal  = (ticket) => { setSelectedTicket(ticket); setModalError(''); };
  const closeTicketModal = ()       => { setSelectedTicket(null);   setModalError(''); };

  const handleSolveTicket = async () => {
    if (!selectedTicket) return;
    setModalError('');
    setSolvingId(selectedTicket.id);
    try {
      const response = await fetch(`${API_BASE}/update_ticket_status.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticket_id: selectedTicket.id, status: 'Resolved' }),
      });
      const data = await response.json();
      if (!data.success) { setModalError(data.message || 'Failed to mark as solved'); return; }
      setTickets((prev) =>
        prev.map((t) =>
          Number(t.ticket_id) === Number(selectedTicket.id) ? { ...t, status: 'Resolved' } : t
        )
      );
      setSelectedTicket((prev) => (prev ? { ...prev, status: 'Resolved' } : prev));
    } catch (err) {
      setModalError(`Failed to mark as solved: ${err.message}`);
    } finally {
      setSolvingId(null);
    }
  };

  // ── Reusable table component ───────────────────────────────────────────
  const TicketTable = ({ rows, idPrefix = '', emptyLabel = 'No items found.' }) => (
    <div className="overflow-x-auto rounded-2xl border border-[#e7d8ce]">
      <table className="w-full min-w-[640px] border-collapse">
        <thead>
          <tr className="bg-[#F5EDE7]">
            {['ID', 'User', 'Branch', 'Subject', 'Priority', 'Status', 'Date', 'Actions'].map((h) => (
              <th key={h} className="px-4 sm:px-6 py-3 text-left text-xs font-bold tracking-wide text-gray-700 whitespace-nowrap">
                {h.toUpperCase()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white">
          {rows.map((ticket, index) => (
            <tr
              key={`${idPrefix}${ticket.id}`}
              className={`border-t border-[#f1e6df] transition-colors hover:bg-[#fff8f3] ${
                index % 2 === 0 ? 'bg-white' : 'bg-[#fffdfa]'
              }`}
            >
              <td className="px-4 sm:px-6 py-3 text-sm font-semibold text-gray-700 whitespace-nowrap">#{ticket.id}</td>
              <td className="px-4 sm:px-6 py-3 text-sm font-medium text-gray-700 whitespace-nowrap">{ticket.user}</td>
              <td className="px-4 sm:px-6 py-3 text-sm text-gray-700 whitespace-nowrap">{ticket.branch}</td>
              <td className="px-4 sm:px-6 py-3 text-sm text-gray-700 font-medium max-w-[160px] truncate">{ticket.subject}</td>
              <td className="px-4 sm:px-6 py-3 text-sm whitespace-nowrap">
                <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-semibold ${getPriorityColor(ticket.priority)}`}>
                  {ticket.priority}
                </span>
              </td>
              <td className="px-4 sm:px-6 py-3 text-sm whitespace-nowrap">
                <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(ticket.status)}`}>
                  {ticket.status}
                </span>
              </td>
              <td className="px-4 sm:px-6 py-3 text-sm text-gray-700 whitespace-nowrap">{ticket.date}</td>
              <td className="px-4 sm:px-6 py-3 text-sm">
                <button
                  type="button"
                  onClick={() => openTicketModal(ticket)}
                  className="rounded-lg bg-[#1f7a8c] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#176272] whitespace-nowrap"
                >
                  View
                </button>
              </td>
            </tr>
          ))}

          {loading && (
            <tr>
              <td colSpan={8} className="px-6 py-8 text-center text-sm text-gray-500">Loading tickets...</td>
            </tr>
          )}
          {!!error && !loading && (
            <tr>
              <td colSpan={8} className="px-6 py-8 text-center text-sm text-red-600">{error}</td>
            </tr>
          )}
          {!loading && !error && rows.length === 0 && (
            <tr>
              <td colSpan={8} className="px-6 py-8 text-center text-sm text-gray-500">{emptyLabel}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <div className="flex flex-col sm:flex-row min-h-screen hygea-app-bg">
        <AdminSidebar />

        {/* Main content */}
        <div className="flex-1 flex flex-col min-w-0">

          {/* ── Top bar ── */}
          <div className="bg-white shadow-sm px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex flex-wrap justify-between items-center gap-3">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Ticket Management</h1>
            <img src={hygeaLogo} alt="Hygea Wellness Spa" className="h-9 sm:h-12" />
          </div>

          <div className="flex-1 p-4 sm:p-6 lg:p-8">
            <div className="rounded-2xl sm:rounded-3xl border border-[#e8d9cf] bg-white p-4 sm:p-6 lg:p-8 shadow-[0_14px_35px_rgba(0,0,0,0.08)]">

              {/* ── Stats grid: 1 col → 3 col ── */}
              <div className="mb-5 sm:mb-6 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <div className="rounded-2xl border border-[#eadfd8] bg-[#fdfaf7] p-4 sm:p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#b07b60]">Tickets</p>
                  <p className="mt-2 text-2xl sm:text-3xl font-bold text-gray-800">{ticketCount}</p>
                </div>
                <div className="rounded-2xl border border-[#eadfd8] bg-[#fdfaf7] p-4 sm:p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#b07b60]">Requests</p>
                  <p className="mt-2 text-2xl sm:text-3xl font-bold text-gray-800">{requestCount}</p>
                </div>
                <div className="rounded-2xl border border-[#eadfd8] bg-[#fdfaf7] p-4 sm:p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#b07b60]">Pending / Resolved</p>
                  <p className="mt-2 text-2xl sm:text-3xl font-bold text-gray-800">{pendingCount} / {resolvedCount}</p>
                </div>
              </div>

              {/* ── Search + filter bar ── */}
              <div className="mb-5 sm:mb-6 rounded-2xl border border-[#eadfd8] bg-[#fdfaf7] px-4 py-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="relative w-full sm:max-w-[480px]">
                    <input
                      type="text"
                      placeholder="Search by user, branch, subject, or ID..."
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      className="w-full rounded-full border border-[#ddcfc4] bg-white px-4 py-2.5 pl-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#B89F80]"
                    />
                    <svg className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
                    </svg>
                  </div>

                  <div className="flex items-center gap-3">
                    <label className="text-sm font-semibold text-gray-700 whitespace-nowrap">Status</label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="rounded-full border border-[#ddcfc4] bg-white px-4 py-2 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#B89F80]"
                    >
                      <option value="All">All</option>
                      <option value="Pending">Pending</option>
                      <option value="Resolved">Resolved</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* ── Tickets table ── */}
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-base sm:text-lg font-bold text-gray-800">Tickets</h2>
                <span className="text-xs font-medium uppercase tracking-wide text-[#9a7f73]">
                  Showing {ticketRows.length} of {ticketCount}
                </span>
              </div>
              <div className="mb-6 sm:mb-8">
                <TicketTable rows={ticketRows} emptyLabel="No tickets found." />
              </div>

              {/* ── Requests table ── */}
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-base sm:text-lg font-bold text-gray-800">Requests</h2>
                <span className="text-xs font-medium uppercase tracking-wide text-[#9a7f73]">
                  Showing {requestRows.length} of {requestCount}
                </span>
              </div>
              <TicketTable rows={requestRows} idPrefix="request-" emptyLabel="No requests found." />

            </div>
          </div>
        </div>
      </div>

      {/* ── Ticket Detail Modal ── */}
      {selectedTicket && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-0 sm:px-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closeTicketModal} />

          <div className="relative z-10 w-full sm:max-w-3xl max-h-[95dvh] sm:max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl border border-[#ecd9ce] bg-gradient-to-br from-[#fffdfa] via-[#fff8f1] to-[#f8efe6] shadow-[0_30px_70px_rgba(0,0,0,0.25)] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">

            {/* Modal header */}
            <div className="flex items-start justify-between border-b border-[#efdccc] px-5 sm:px-6 py-4 sm:py-5">
              <div className="pr-4 min-w-0">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#9d7662]">Ticket Details</p>
                <h3 className="mt-1 text-lg sm:text-2xl font-bold text-[#4f372f] truncate">
                  #{selectedTicket.id} {selectedTicket.subject}
                </h3>
              </div>
              <button
                type="button"
                onClick={closeTicketModal}
                className="shrink-0 rounded-lg border border-[#e6d1c3] bg-white px-3 py-1.5 text-sm font-semibold text-[#6a4b3f] hover:bg-[#faf2eb]"
              >
                Close
              </button>
            </div>

            {/* Modal body — 1 col on mobile, 2 col on md+ */}
            <div className="grid grid-cols-1 gap-4 sm:gap-5 px-5 sm:px-6 py-5 sm:py-6 md:grid-cols-2">
              <div className="rounded-2xl border border-[#ead8cb] bg-white/85 p-4">
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#a17d69]">Submitted By</p>
                <p className="mt-1 text-sm font-semibold text-[#4f372f]">{selectedTicket.user}</p>
                <p className="mt-1 text-xs text-[#7f675c]">{selectedTicket.branch}</p>
              </div>

              <div className="rounded-2xl border border-[#ead8cb] bg-white/85 p-4">
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#a17d69]">Status</p>
                <div className="mt-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(selectedTicket.status)}`}>
                    {selectedTicket.status}
                  </span>
                </div>
              </div>

              <div className="md:col-span-2 rounded-2xl border border-[#ead8cb] bg-white/85 p-4">
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#a17d69]">Description</p>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-[#4d403a]">{selectedTicket.message}</p>
              </div>

              <div className="md:col-span-2 rounded-2xl border border-[#ead8cb] bg-white/85 p-4">
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#a17d69] mb-2">Image</p>
                {selectedTicket.hasAttachment ? (
                  <img
                    src={`${API_BASE}/get_ticket_image.php?ticket_id=${selectedTicket.id}`}
                    alt="Ticket attachment"
                    className="max-h-[280px] sm:max-h-[320px] w-auto rounded-xl border border-[#e3d2c7] object-contain bg-white"
                  />
                ) : (
                  <p className="text-sm text-[#7f675c]">No image attached.</p>
                )}
              </div>
            </div>

            {modalError && (
              <div className="mx-5 sm:mx-6 mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
                {modalError}
              </div>
            )}

            {/* Modal footer */}
            <div className="flex flex-wrap items-center justify-end gap-3 border-t border-[#efdccc] px-5 sm:px-6 py-4 bg-white/70">
              <button
                type="button"
                onClick={closeTicketModal}
                className="rounded-xl border border-[#dfcdbf] bg-white px-4 py-2 text-sm font-semibold text-[#5f4a41] hover:bg-[#f9f2ec]"
              >
                Close
              </button>
              <button
                type="button"
                onClick={handleSolveTicket}
                disabled={selectedTicket.status === 'Resolved' || solvingId === selectedTicket.id}
                className="rounded-xl bg-[#2f7a50] px-5 py-2 text-sm font-semibold text-white hover:bg-[#276744] disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap"
              >
                {solvingId === selectedTicket.id
                  ? 'Updating...'
                  : selectedTicket.status === 'Resolved'
                    ? 'Solved'
                    : 'Mark as Solved'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TicketManagement;
